export interface User {
  id: string;
  email: string;
}

export interface AuthResponse {
  user: User;
  access_token?: string;
  expires_at?: number;
  message?: string;
}

export type TicketTier =
  | "party_not_presents"
  | "inner_circle_plus_more"
  | "venue_showcase"
  | "ghost_seats";

export type GuestTicketTier =
  | "party_not_presents"
  | "inner_circle_plus_more"
  | "venue_showcase";

export type DressCode =
  | "black_tie"
  | "formal"
  | "cocktail"
  | "smart_casual"
  | "casual"
  | "bohemian";

export type VibeTag =
  | "party"
  | "intimate"
  | "elegant"
  | "cultural"
  | "outdoor"
  | "destination";

export type ListingStatus = "draft" | "live" | "sold_out" | "past";
export type RsvpMode = "restricted" | "open";

export interface TicketTierRow {
  id: string;
  wedding_id: string;
  tier_type: TicketTier;
  label?: string;
  price_cents: number;
  capacity: number;
  sold_count: number;
  close_date?: string | null;
  is_active: boolean;
  created_at?: string;
}

export interface Wedding {
  id: string;
  title: string;
  date: string;
  description?: string;
  location?: string;
  couple_name_1?: string | null;
  couple_name_2?: string | null;
  couple_photo_url?: string | null;
  story?: string | null;
  venue_name?: string | null;
  venue_address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  /** True when unauthenticated / non-invitee GET of a non-Discover listing. */
  redacted?: boolean;
  dress_code?: DressCode | null;
  vibe_tags?: VibeTag[];
  rsvp_mode?: RsvpMode;
  sale_close_date?: string | null;
  listing_status?: ListingStatus;
  requires_21_plus?: boolean;
  is_public: boolean;
  allow_ghost_seats: boolean;
  ticket_price: number | null;
  created_by?: string;
  created_at: string;
  ticket_tiers?: TicketTierRow[];
}

export interface DashboardListing extends Wedding {
  pending_approvals: number;
  rsvp_stats: { confirmed: number; pending: number; declined: number };
  revenue_total_cents: number;
  revenue_by_tier: Record<string, number>;
}

export interface DashboardResponse {
  listings: DashboardListing[];
  totals: {
    pending_approvals: number;
    revenue_total_cents: number;
    wedding_count: number;
  };
}

export interface GoerProfile {
  user_id: string;
  dress_comfort: DressCode[];
  vibe_preferences: VibeTag[];
  is_21_plus: boolean;
  price_min_cents: number;
  price_max_cents: number | null;
  instagram_handle?: string | null;
  complete?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Guest {
  id: string;
  wedding_id: string;
  full_name: string;
  email?: string;
  phone?: string;
  group_id?: string;
  invited_by?: string;
  ticket_tier?: GuestTicketTier;
  ticket_tier_id?: string | null;
  plus_one_limit?: number;
  is_plus_one?: boolean;
  parent_guest_id?: string;
  created_at: string;
}

export interface Rsvp {
  id: string;
  wedding_id: string;
  guest_id: string;
  status: "pending" | "confirmed" | "declined";
  checked_in: boolean;
  created_at: string;
}

export interface GuestWithRsvp extends Guest {
  rsvp: Rsvp | null;
}

export interface ManageRsvpPlusOne {
  id: string;
  full_name: string;
  email?: string | null;
  payment_status: "paid" | "unpaid";
  rsvp_status: "pending" | "confirmed" | "declined";
}

export interface ManageRsvpResponse {
  rsvp_id: string;
  wedding_id: string;
  status: string;
  viewer?: "primary" | "plus_one";
  eligible: boolean;
  remaining_allotment: number;
  plus_one_limit: number;
  plus_one_price_cents: number;
  wedding: {
    id: string;
    title: string;
    date: string;
    couple_name_1?: string | null;
    couple_name_2?: string | null;
    venue_name?: string | null;
    city?: string | null;
    state?: string | null;
    location?: string | null;
  };
  primary_guest: {
    id: string;
    full_name: string;
    email?: string | null;
    plus_one_limit: number;
  };
  plus_ones: ManageRsvpPlusOne[];
}

export interface GuestRequest {
  id: string;
  wedding_id: string;
  user_id: string;
  user_email?: string;
  message?: string;
  status: "pending" | "approved" | "declined" | "expired";
  expires_at?: string;
  created_at: string;
  goer_profile?: GoerProfile | null;
  ticket_tier_id?: string | null;
}

export interface CreateWeddingPayload {
  title: string;
  date: string;
  description?: string;
  location?: string;
  couple_name_1?: string;
  couple_name_2?: string;
  couple_photo_url?: string;
  story?: string;
  venue_name?: string;
  venue_address?: string;
  city?: string;
  state?: string;
  zip?: string;
  dress_code?: DressCode | "";
  vibe_tags?: VibeTag[];
  rsvp_mode?: RsvpMode;
  sale_close_date?: string;
  listing_status?: ListingStatus;
  /** Edit-only: mark listing sold out (or clear sold-out while date is upcoming). */
  mark_sold_out?: boolean;
  requires_21_plus?: boolean;
  is_public?: boolean;
  allow_ghost_seats?: boolean;
  ticket_price?: number;
  ghost_capacity?: number;
  capacity?: number;
  tiers?: Array<{
    tier_type: TicketTier;
    price_cents: number;
    capacity: number;
    close_date?: string;
    is_active?: boolean;
  }>;
}

export interface MatchRsvpPayload {
  wedding_id: string;
  full_name: string;
  email?: string;
}

export interface ConfirmRsvpPayload {
  rsvp_id: string;
  status: "confirmed" | "declined";
  phone?: string;
}

export interface ConfirmRsvpResponse {
  id: string;
  guest_id: string;
  status: string;
  wedding_id: string;
  ticket_tier: GuestTicketTier;
  plus_one_limit: number;
  requires_payment: boolean;
  payment_type: "ticket" | "plus_ones" | null;
  requires_plus_ones: boolean;
}

export interface CreateRequestPayload {
  wedding_id: string;
  message?: string;
}

export interface PlusOneEntry {
  full_name: string;
  email?: string;
}

export interface AddGuestPayload {
  wedding_id: string;
  full_name: string;
  email?: string;
  phone?: string;
  group_id?: string;
  invited_by?: string;
  ticket_tier?: GuestTicketTier;
  plus_one_limit?: number;
}

export interface BulkAddGuestEntry {
  full_name: string;
  email?: string;
  ticket_tier?: GuestTicketTier;
  plus_one_limit?: number;
}

export interface BulkAddGuestsPayload {
  wedding_id: string;
  guests: BulkAddGuestEntry[];
}

export interface BulkAddGuestsResponse {
  guests: Guest[];
  rsvps: Rsvp[];
  count: number;
  email_warning?: string | null;
}

export interface CheckoutPayload {
  wedding_id: string;
  rsvp_id?: string;
  request_id?: string;
  payment_type?: "ticket" | "plus_ones" | "request";
  plus_ones?: PlusOneEntry[];
  plus_one_guest_ids?: string[];
}

export interface MatchRsvpResponse {
  matched: boolean;
  guest_id: string;
  rsvp_id: string;
  status: string;
  requires_confirmation: boolean;
  ticket_tier: GuestTicketTier;
  plus_one_limit: number;
  open_created?: boolean;
  match_score?: number;
  is_plus_one?: boolean;
  manage_rsvp_id?: string | null;
}

export interface CheckoutResponse {
  sessionId: string;
  url: string;
}

export interface DiscoverFilters {
  dress_code?: string;
  vibe?: string;
  requires_21_plus?: boolean;
  price_min?: number;
  price_max?: number;
  last_minute?: boolean;
  city?: string;
  state?: string;
}

export const DRESS_CODE_LABELS: Record<DressCode, string> = {
  black_tie: "Black Tie",
  formal: "Formal",
  cocktail: "Cocktail",
  smart_casual: "Smart Casual",
  casual: "Casual",
  bohemian: "Bohemian",
};

export const VIBE_TAG_LABELS: Record<VibeTag, string> = {
  party: "Party",
  intimate: "Intimate",
  elegant: "Elegant",
  cultural: "Cultural",
  outdoor: "Outdoor",
  destination: "Destination",
};

export const TICKET_TIER_LABELS: Record<TicketTier, string> = {
  party_not_presents: "Guest-Funded Seats",
  inner_circle_plus_more: "Extended Guest Seats",
  venue_showcase: "Industry Preview Seats",
  ghost_seats: "Public Seats (Approval Required)",
};

export const TICKET_TIER_DESCRIPTIONS: Record<TicketTier, string> = {
  party_not_presents:
    "For invited guests who choose to pay for their seat instead of giving a gift. No approval needed, guests RSVP and pay in one step.",
  inner_circle_plus_more:
    "For invited guests whose coverage varies, some seats are covered by the couple, others (including plus-ones) are paid by the guest at RSVP. No approval required.",
  venue_showcase:
    "For venues, planners, or vendors sending prospective clients to experience the wedding firsthand. No approval needed once added to the guest list.",
  ghost_seats:
    "Open to strangers who find the wedding on the Discover page. Every request needs the couple's approval before payment is collected.",
};

export const GUEST_TIER_OPTIONS: GuestTicketTier[] = [
  "party_not_presents",
  "inner_circle_plus_more",
  "venue_showcase",
];
